import { IANAZone } from "luxon";

import { ISlotUpdateEvent } from "../Components/BookingWizard/BookingWizard";
import { DateTime } from "../Types/DateTime";
import { BlockType } from "./Block";
import { ServiceScheduleDay } from "./Calendar";
import { OpsServiceCategory } from "./OpsService";
import { EnhancedOpsSlot, Slot } from "./OpsSlot";
import { ServiceGroup } from "./ServiceGroup";

/** A calendar for a particular center from the OPS. */
export class OpsCalendar {
  /** The list of services. */
  services!: OpsServiceCategory[];
  /** The slots for each day in the calendar, keyed by the date in ISO format. */
  days!: Record<string, Slot[] | undefined>;
  /** The services in the calendar. */
  calendarServices!: ServiceGroup[];
}

/**
 * Generate an array of dates between two dates
 * @param startDate The start date in ISO format.
 * @param endDate The end date in ISO format.
 */
const generateDateArray = (startDate: string, endDate: string): string[] => {
  if (!startDate || !endDate) return [];

  const start = DateTime.fromISO(startDate);
  const end = DateTime.fromISO(endDate);
  const dates: string[] = [];

  let current = start;
  while (current <= end) {
    dates.push(current.toISODate());
    current = current.plus({ days: 1 });
  }

  return dates;
};

export class EnhancedOpsCalendar extends OpsCalendar {
  /** A dictionary of enhanced service schedules for active services, keyed by the service id. */
  serviceSchedules: Record<string, ServiceScheduleDay<EnhancedOpsSlot>[]>;
  /** The time zone of the center which this calendar is representing. */
  timeZone: IANAZone;

  constructor(calendar: OpsCalendar, centerTimeZone: IANAZone, slotUpdateEvent?: ISlotUpdateEvent) {
    super();

    this.timeZone = centerTimeZone;
    this.serviceSchedules = {};

    const { days, services } = calendar;
    this.services = services;
    this.days = days;

    // if there are days, we need to fill in any gaps
    // this is used to create greyed out days in the calendar
    const oldDays = Object.keys(this.days || {});
    if (this.days && oldDays.length > 1) {
      const datesInBetween = generateDateArray(oldDays[0], oldDays.slice(-1)[0]);

      datesInBetween.forEach(dateKey => {
        if (!this.days[dateKey]) {
          this.days[dateKey] = [];
        }
      });
    }

    services.forEach(availableService => {
      // only build enhanced calendars for services which are active
      if (!Object.values(this.days).some(blocks => blocks)) return;

      // need to deep copy entries to set days correctly without modifying the original object
      const entries = Object.entries(JSON.parse(JSON.stringify(this.days)) as Record<string, Slot[] | undefined>)?.sort(
        (a, b) => DateTime.fromISO(a[0]).toMillis() - DateTime.fromISO(b[0]).toMillis()
      );
      calendar.days = {};

      const scheduleDays: ServiceScheduleDay<EnhancedOpsSlot>[] = entries.map(([key, value]) => {
        // if a day doesn't have slots, it has booked the entire day
        // so we need to create a day with no slots and a blocked state
        if (this.days[key]?.length === 0) {
          const isoKeyDate = DateTime.fromISO(key, { zone: "utc" }).toISODate();
          const date = DateTime.fromISO(isoKeyDate, {
            zone: centerTimeZone.name
          });

          return {
            blocks: [],
            date: date,
            dayState: BlockType.Blocked
          };
        }

        let slots = value?.length
          ? value.map(
              slot =>
                new EnhancedOpsSlot(
                  // times come in as strings, so we need to cast them to DateTime
                  // and adjust them to the center's time zone
                  // times are like 15:00:00.000
                  {
                    ...slot,
                    startTimeKey: key + "T" + slot.startTime,
                    endTimeKey: key + "T" + slot.endTime
                  },
                  centerTimeZone
                )
            )
          : [];

        if (slotUpdateEvent && slots.length > 0) {
          const { isSlotReservedByCustomer, slotId, type } = slotUpdateEvent;

          slots = slots.map(slot => {
            if (Number(slot.id) === slotId) {
              if (isSlotReservedByCustomer) return { ...slot, type: BlockType.Open };

              return { ...slot, type };
            }

            return slot;
          });
        }

        // In order to refresh without losing the state of the
        // previous days, we need to merge the previous days with the
        // new ones
        const previousDays = [...(this.days[key] || [])];
        this.days[key] = slots.map(s => {
          const previousDay = previousDays.find(p => p.id === s.id);
          if (s.id === previousDay?.id) {
            return { ...previousDay, type: s.type };
          }

          return (
            previousDay
              ? { ...previousDay }
              : {
                  id: s.id,
                  endTime: s.endTimeKey?.split("T")[1],
                  startTime: s.startTimeKey?.split("T")[1],
                  type: s.type
                }
          ) as Slot;
        });

        const isoKeyDate = DateTime.fromISO(key, { zone: "utc" }).toISODate();
        const date = DateTime.fromISO(isoKeyDate, {
          zone: centerTimeZone.name
        });
        const dayState = slots.reduce(
          (least, block) => (block.type < least ? block.type : least),
          BlockType.Blocked as BlockType
        );

        return {
          blocks: slots,
          date,
          // the lowest value (most available) state from the blocks
          dayState
        };
      });

      this.serviceSchedules[availableService.id] = scheduleDays;
    });

    this.calendarServices = this.services.map(service => ({
      service,
      // a service is disabled if we did not create a schedule for it
      disabled: !this.serviceSchedules[service.id]
    }));
  }
}
